import {
    computed,
    type MaybeRef,
    type Ref,
    type ShallowRef,
    type WritableComputedRef,
} from 'vue'
import type { GalleryCarouselCardFragment } from '@/graphql/api'
import type { AnimationFrameFunction } from './useCarouselNew'
import { useCarousel } from './useCarouselNew'
import { type ExtendedSlide, useEndlessSlides } from './useEndlessSlides'
import type { ArgumentsType } from '@/helpers'
import type { Promisify } from '@vueuse/core'

interface CarouselEndless {
    current: WritableComputedRef<number, number>
    next: () => void
    previous: () => void
    slidesEndless: Ref<ExtendedSlide[]>
    container: Ref<HTMLElement | null>
    elements: Readonly<ShallowRef<HTMLDivElement[] | null>>
    itemsAnimated: Ref<HTMLElement[]>
    recalculateSlidesAndRescroll: (
        ...args: ArgumentsType<() => void>
    ) => Promisify<ReturnType<() => void>>
    handleTouchStart: () => boolean
    handleTouchEnd: () => boolean
    isUserInteracting: Ref<boolean>
}

export function useCarouselEndless(
    isCentered: MaybeRef<boolean>,
    slides: Ref<GalleryCarouselCardFragment[]>,
    animation?: AnimationFrameFunction
): CarouselEndless {
    const {
        previous,
        next,
        current,
        slideWidth,
        container,
        containerOffsetWidth,
        elements,
        itemsAnimated,
    } = useCarousel(
        computed(() => slides.value.length),
        { isCentered, animation }
    )

    const {
        slidesEndless,
        recalculateSlidesAndRescroll,
        handleTouchStart,
        handleTouchEnd,
        isUserInteracting,
    } = useEndlessSlides({
        slides,
        current,
        slideWidth,
        container,
        containerOffsetWidth,
    })

    return {
        current,
        next,
        previous,
        slidesEndless,
        container,
        elements,
        itemsAnimated,
        recalculateSlidesAndRescroll,
        handleTouchStart,
        handleTouchEnd,
        isUserInteracting,
    }
}
